"use client";

import { format } from "date-fns";
import MapPickerModal from "@routecore/map-picker";
import { CalendarPlus, CheckCircle2, Clock, MapPin, Pencil, Plus, Waypoints, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import {
  cn,
  formatTime,
  ordinal,
  parseIso,
  withAlpha,
  EMPLOYEE_COLORS,
  STATUS_META,
  type Employee,
  type Itinerary,
  type PickedLocation,
  type Status,
  type Team,
} from "@routecore/core";
import { Modal, ModalHeader } from "@routecore/modal";
import {
  ConfirmDelete,
  EmployeeDot,
  Field,
  GhostButton,
  PrimaryButton,
  StatusPill,
  StyledSelect,
  inputCls,
} from "@routecore/controls";
import type { Toast } from "@routecore/toasts";
import type { CurrentUser } from "@routecore/core";


/* ------------------------------ employee modal ----------------------------- */

export type EmployeeFormData = {
  name: string;
  role: string;
  color: string;
  teamId: number | null;
  boundAccountId: string | null;
  boundAccountName: string | null;
  boundAccountEmail: string | null;
};

export type AdminAccount = {
  id: string;
  full_name: string;
  email: string;
  role: string;
};

export function EmployeeModal({
  open,
  employee,
  usedColors,
  teams,
  employees,
  onClose,
  onSave,
  onManageTeams,
  readOnly,
  isPrivileged,
}: {
  open: boolean;
  employee: Employee | null;
  usedColors: string[];
  teams: Team[];
  employees: AdminAccount[];
  onClose: () => void;
  onSave: (data: EmployeeFormData) => void;
  onManageTeams: () => void;
  readOnly?: boolean;
  isPrivileged?: boolean;
}) {
  const [name, setName] = useState("");
  const [role, setRole] = useState("");
  const [color, setColor] = useState(EMPLOYEE_COLORS[0]);
  const [teamId, setTeamId] = useState("");
  const [boundAccountId, setBoundAccountId] = useState("");

  useEffect(() => {
    if (!open) return;
    setName(employee?.name ?? "");
    setRole(employee?.role ?? "");
    setColor(employee?.color ?? EMPLOYEE_COLORS.find((c) => !usedColors.includes(c)) ?? EMPLOYEE_COLORS[0]);
    setTeamId(employee?.teamId ? String(employee.teamId) : "");
    setBoundAccountId(employee?.boundAccountId ?? "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, employee]);

  const submit = () => {
    if (readOnly) return;
    if (!name.trim()) return;
    const admin = employees.find((a) => a.id === boundAccountId) ?? null;
    onSave({
      name: name.trim(),
      role: role.trim(),
      color,
      teamId: teamId ? Number(teamId) : null,
      boundAccountId: boundAccountId || null,
      boundAccountName: admin?.full_name ?? null,
      boundAccountEmail: admin?.email ?? null,
    });
  };

  const boundOwnerName = employee
    ? employee.boundAccountName ??
      (employee.boundAccountEmail
        ? employees.find((a) => a.email?.toLowerCase() === employee.boundAccountEmail?.toLowerCase())?.full_name ??
          employee.boundAccountEmail
        : "another account")
    : "";

  return (
    <Modal open={open} onClose={onClose}>
      <ModalHeader
        eyebrow={employee ? (readOnly ? "View profile" : "Edit profile") : "New recruit"}
        title={employee ? employee.name : "Add employee"}
        onClose={onClose}
      />
      <div className="space-y-4">
        <Field label="Full name">
          <input
            autoFocus={!readOnly}
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => !readOnly && e.key === "Enter" && submit()}
            placeholder="e.g. Jordan Marchetti"
            readOnly={readOnly}
            disabled={readOnly}
            className={cn(inputCls, readOnly && "opacity-80 cursor-not-allowed")}
          />
        </Field>
        <Field label="Role / position">
          <input
            value={role}
            onChange={(e) => setRole(e.target.value)}
            onKeyDown={(e) => !readOnly && e.key === "Enter" && submit()}
            placeholder="e.g. Field Operations Lead"
            readOnly={readOnly}
            disabled={readOnly}
            className={cn(inputCls, readOnly && "opacity-80 cursor-not-allowed")}
          />
        </Field>
        <div>
          <div className="mb-1.5 flex items-center justify-between">
            <span className="text-[11px] font-bold tracking-[0.14em] text-ink-soft uppercase">
              Team
            </span>
            {!readOnly && isPrivileged && (
              <button
                type="button"
                onClick={onManageTeams}
                className="inline-flex items-center gap-1 text-[11px] font-bold text-accent transition-opacity hover:opacity-70"
              >
                <Plus className="size-3" /> New team
              </button>
            )}
          </div>
          <StyledSelect value={teamId} onChange={setTeamId} disabled={readOnly}>
            <option value="">No team — unassigned</option>
            {teams.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </StyledSelect>
          {teamId && (
            <div className="mt-2 flex items-center gap-1.5">
              <span
                className="size-2.5 rounded-full"
                style={{ backgroundColor: teams.find((t) => String(t.id) === teamId)?.color }}
              />
              <span className="text-[11px] font-semibold text-ink-soft">
                Assigned to {teams.find((t) => String(t.id) === teamId)?.name}
              </span>
            </div>
          )}
        </div>
        <Field label="Bind to Account">
          <StyledSelect
            value={boundAccountId}
            onChange={setBoundAccountId}
            disabled={readOnly || !isPrivileged}
          >
            <option value="">No account — unbound</option>
            {employees.map((a) => (
              <option key={a.id} value={a.id}>
                {a.full_name || a.email}
                {a.email && a.full_name ? ` (${a.email})` : ""}
              </option>
            ))}
          </StyledSelect>
          {boundAccountId && (
            <div className="mt-2 flex items-center gap-1.5">
              <span className="inline-flex size-2.5 shrink-0 items-center justify-center rounded-full bg-accent text-white">
                <svg viewBox="0 0 24 24" className="size-1.5" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 6 9 17l-5-5" />
                </svg>
              </span>
              <span className="text-[11px] font-semibold text-ink-soft">
                Bound to {employees.find((a) => a.id === boundAccountId)?.full_name ?? employees.find((a) => a.id === boundAccountId)?.email}
              </span>
            </div>
          )}
        </Field>
        <Field label="Trail color">
          <div className="flex flex-wrap items-center gap-2.5 pt-1">
            {EMPLOYEE_COLORS.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => !readOnly && setColor(c)}
                title={c}
                className={cn(
                  "size-8 rounded-full transition-all",
                  readOnly ? "cursor-not-allowed" : "active:scale-90",
                  color === c
                    ? "scale-110 ring-2 ring-ink ring-offset-2 ring-offset-porcelain"
                    : readOnly
                      ? ""
                      : "hover:scale-110",
                )}
                style={{ backgroundColor: c }}
                disabled={readOnly}
              />
            ))}
          </div>
        </Field>
        <div className="flex items-center gap-2 pt-2">
          <PrimaryButton
            onClick={submit}
            icon={CheckCircle2}
            disabled={readOnly}
            className="flex-1 justify-center"
          >
            {employee
              ? readOnly
                ? `🔒 Locked — bound to ${boundOwnerName}`
                : "Save changes"
              : "Add to roster"}
          </PrimaryButton>
          <GhostButton onClick={onClose}>{readOnly ? "Close" : "Cancel"}</GhostButton>
        </div>
      </div>
    </Modal>
  );
}

/* ----------------------------- itinerary modal ----------------------------- */

export type ItineraryFormData = {
  employeeId: number;
  title: string;
  date: string;
  appointmentTime: string;
  notes: string;
  status: Status;
  seq: number;
  locationName: string;
  lat: number | null;
  lng: number | null;
};

export function ItineraryModal({
  open,
  itinerary,
  presetDate,
  presetEmployeeId,
  employees,
  itineraries,
  onClose,
  onSave,
  canEditEmployee,
  isPrivileged,
}: {
  open: boolean;
  itinerary: Itinerary | null;
  presetDate?: string;
  presetEmployeeId?: number;
  employees: Employee[];
  itineraries: Itinerary[];
  onClose: () => void;
  onSave: (data: ItineraryFormData) => void;
  canEditEmployee?: (emp: Pick<Employee, "id" | "boundAccountId" | "boundAccountEmail">) => boolean;
  isPrivileged?: boolean;
}) {
  const writableEmployees = useMemo(() => {
    if (isPrivileged) return employees;
    if (canEditEmployee) return employees.filter((e) => canEditEmployee(e));
    return employees;
  }, [employees, isPrivileged, canEditEmployee]);
  const [employeeId, setEmployeeId] = useState("");
  const [title, setTitle] = useState("");
  const [date, setDate] = useState("");
  const [notes, setNotes] = useState("");
  const [seq, setSeq] = useState(1);
  const [seqTouched, setSeqTouched] = useState(false);
  const [location, setLocation] = useState<PickedLocation | null>(null);
  const [mapOpen, setMapOpen] = useState(false);
  const [time, setTime] = useState("");

  useEffect(() => {
    if (!open) return;
    const preferred = [itinerary?.employeeId, presetEmployeeId, writableEmployees[0]?.id]
      .filter(Boolean)
      .map((v) => Number(v))
      .find((v) => writableEmployees.some((e) => e.id === v));
    setEmployeeId(String(preferred ?? writableEmployees[0]?.id ?? ""));
    setTitle(itinerary?.title ?? "");
    setDate(itinerary?.date ?? presetDate ?? "");
    setNotes(itinerary?.notes ?? "");
    setTime(itinerary?.appointmentTime ?? "");
    setSeq(itinerary?.seq ?? 1);
    setSeqTouched(false);
    setLocation(
      itinerary && itinerary.lat != null && itinerary.lng != null
        ? { name: itinerary.locationName, lat: itinerary.lat, lng: itinerary.lng }
        : null,
    );
    setMapOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, itinerary, presetDate, presetEmployeeId, writableEmployees]);

  /** Next free stop number for the chosen employee + date. */
  const suggestedSeq = useMemo(() => {
    let maxSeq = 0;
    const empId = Number(employeeId);
    for (const it of itineraries) {
      if (it.employeeId === empId && it.date === date && it.id !== itinerary?.id) {
        maxSeq = Math.max(maxSeq, it.seq);
      }
    }
    return maxSeq + 1;
  }, [itineraries, employeeId, date, itinerary]);

  // Keep the sequence following the suggestion until the user overrides it.
  useEffect(() => {
    if (open && !seqTouched && !itinerary) setSeq(suggestedSeq);
  }, [suggestedSeq, open, seqTouched, itinerary]);

  const seqOptions = useMemo(() => {
    const max = Math.max(suggestedSeq + 1, seq, 4);
    return Array.from({ length: max }, (_, i) => i + 1);
  }, [suggestedSeq, seq]);

  const valid = Boolean(title.trim() && date && employeeId);
  const employeeLocked = !isPrivileged && writableEmployees.length === 1;
  const noWritable = !isPrivileged && writableEmployees.length === 0;

  const submit = () => {
    if (!valid || noWritable) return;
    onSave({
      employeeId: Number(employeeId),
      title: title.trim(),
      date,
      appointmentTime: /^\d{2}:\d{2}$/.test(time) ? time : "",
      notes: notes.trim(),
      status: itinerary?.status ?? "scheduled",
      seq,
      locationName: location?.name ?? "",
      lat: location?.lat ?? null,
      lng: location?.lng ?? null,
    });
  };

  return (
    <>
      <Modal open={open} onClose={onClose}>
        <ModalHeader
          eyebrow={itinerary ? "Edit entry" : "Plot a course"}
          title={itinerary ? "Edit itinerary" : "New itinerary"}
          onClose={onClose}
        />
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Employee">
              <StyledSelect
                value={employeeId}
                onChange={setEmployeeId}
                disabled={noWritable || employeeLocked}
              >
                {writableEmployees.map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.name}
                  </option>
                ))}
              </StyledSelect>
              {noWritable && (
                <div className="mt-2 flex items-start gap-1.5 rounded-lg border border-[#B4443C]/30 bg-[#B4443C]/10 px-2.5 py-2">
                  <span className="inline-flex size-2.5 shrink-0 items-center justify-center rounded-full bg-[#B4443C] text-white mt-0.5">
                    <svg viewBox="0 0 24 24" className="size-1.5" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 9v4M12 17h.01" />
                      <circle cx="12" cy="12" r="10" />
                    </svg>
                  </span>
                  <p className="text-[10px] font-semibold leading-tight text-[#B4443C]">
                    No employee profile is bound to your account. Ask an admin to bind a field profile before creating itineraries.
                  </p>
                </div>
              )}
              {employeeLocked && (
                <div className="mt-2 flex items-center gap-1.5">
                  <span className="inline-flex size-2.5 shrink-0 items-center justify-center rounded-full bg-accent/80 text-white">
                    🔒
                  </span>
                  <span className="text-[10px] font-semibold text-ink-soft">
                    Only your bound profile can be selected.
                  </span>
                </div>
              )}
            </Field>
            <Field label="Date">
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className={cn(inputCls, "cursor-pointer")}
              />
            </Field>
          </div>

          <Field label="Itinerary title">
            <input
              autoFocus
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Client site visit — Northgate Mall"
              className={inputCls}
            />
          </Field>

          <div>
            <div className="mb-1.5 flex items-center justify-between">
              <span className="text-[11px] font-bold tracking-[0.14em] text-ink-soft uppercase">
                Time of appointment
              </span>
              {time && (
                <button
                  type="button"
                  onClick={() => setTime("")}
                  className="inline-flex items-center gap-1 text-[11px] font-bold text-ink-faint transition-colors hover:text-[#B4443C]"
                >
                  <X className="size-3" /> Clear
                </button>
              )}
            </div>
            <div className="relative">
              <Clock className="pointer-events-none absolute top-1/2 left-3.5 z-10 size-4 -translate-y-1/2 text-ink-faint" />
              <input
                type="time"
                step={300}
                value={time}
                onChange={(e) => setTime(e.target.value)}
                className={cn(inputCls, "cursor-pointer pl-10")}
              />
            </div>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {["08:00", "10:00", "13:00", "15:00", "17:00"].map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setTime(t)}
                  className={cn(
                    "rounded-full border px-2.5 py-1 text-[10px] font-bold transition-all active:scale-95",
                    time === t
                      ? "border-accent bg-accent/10 text-accent"
                      : "border-line text-ink-faint hover:border-ink/30 hover:text-ink",
                  )}
                >
                  {formatTime(t)}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Field label="Stop sequence">
                <StyledSelect
                  value={String(seq)}
                  onChange={(v) => {
                    setSeq(Number(v));
                    setSeqTouched(true);
                  }}
                >
                  {seqOptions.map((n) => (
                    <option key={n} value={n}>
                      {ordinal(n)} stop
                    </option>
                  ))}
                </StyledSelect>
              </Field>
              {!itinerary && suggestedSeq !== seq && (
                <p className="mt-1.5 text-[11px] text-ink-faint">
                  Next suggested: <span className="font-bold">{ordinal(suggestedSeq)}</span>
                </p>
              )}
            </div>
            <Field label="Location">
              <button
                type="button"
                onClick={() => setMapOpen(true)}
                className={cn(
                  "flex w-full items-center justify-center gap-2 rounded-xl border px-3.5 py-2.5 text-sm font-semibold transition-colors",
                  location
                    ? "border-line bg-porcelain text-ink hover:border-accent hover:text-accent"
                    : "border-dashed border-ink-faint text-ink-soft hover:border-accent hover:text-accent",
                )}
              >
                <MapPin className="size-4" />
                {location ? "Change location" : "Add location"}
              </button>
            </Field>
          </div>

          {location && (
            <div className="flex items-center gap-2.5 rounded-xl border border-line bg-paper/70 px-3 py-2.5">
              <span className="grid size-8 shrink-0 place-items-center rounded-lg bg-accent/10 text-accent">
                <MapPin className="size-4" />
              </span>
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-semibold">{location.name}</div>
                <div className="text-[11px] text-ink-faint tabular-nums">
                  {location.lat.toFixed(5)}, {location.lng.toFixed(5)}
                </div>
              </div>
              <button
                type="button"
                title="Remove location"
                onClick={() => setLocation(null)}
                className="grid size-7 shrink-0 place-items-center rounded-md text-ink-faint transition-colors hover:bg-[#B4443C]/10 hover:text-[#B4443C]"
              >
                <X className="size-3.5" />
              </button>
            </div>
          )}

          <Field label="Field notes (optional)">
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="Departure window, contacts, reminders…"
              className={cn(inputCls, "resize-none")}
            />
          </Field>

          <div className="flex items-center gap-2 pt-2">
            <PrimaryButton
              onClick={submit}
              icon={CalendarPlus}
              disabled={!valid || noWritable}
              className="flex-1 justify-center"
            >
              {itinerary
                ? noWritable
                  ? "🔒 No bound profile"
                  : "Save changes"
                : noWritable
                  ? "🔒 No bound profile"
                  : "Pin to calendar"}
            </PrimaryButton>
            <GhostButton onClick={onClose}>Cancel</GhostButton>
          </div>
        </div>
      </Modal>

      {/* nested map picker (opens above this modal) */}
      <MapPickerModal
        open={mapOpen}
        initial={location}
        onClose={() => setMapOpen(false)}
        onSave={(loc) => {
          setLocation(loc);
          setMapOpen(false);
        }}
      />
    </>
  );
}

/* -------------------------------- day modal -------------------------------- */

export function DayModal({
  open,
  date,
  items,
  employees,
  onClose,
  onAdd,
  onEdit,
  onDelete,
  isPrivileged,
  canEditEmployee,
  canEditItinerary,
}: {
  open: boolean;
  date: string | null;
  items: Itinerary[];
  employees?: Employee[];
  onClose: () => void;
  onAdd: () => void;
  onEdit: (item: Itinerary) => void;
  onDelete: (item: Itinerary) => void;
  isPrivileged?: boolean;
  canEditEmployee?: (emp: Pick<Employee, "id" | "boundAccountId" | "boundAccountEmail">) => boolean;
  canEditItinerary?: (it: Pick<Itinerary, "employeeId">) => boolean;
}) {
  const hasWritableEmployee = useMemo(() => {
    if (isPrivileged) return true;
    if (!canEditEmployee || !employees) return true;
    return employees.some((e) => canEditEmployee(e));
  }, [employees, isPrivileged, canEditEmployee]);

  return (
    <Modal open={open && !!date} onClose={onClose}>
      <ModalHeader
        eyebrow="Daily log"
        title={date ? format(parseIso(date), "EEEE, MMMM d") : ""}
        onClose={onClose}
      />
      <div className="max-h-[46vh] space-y-2 overflow-y-auto pr-1">
        {items.length === 0 && (
          <div className="grid place-items-center gap-2 rounded-2xl border border-dashed border-line py-8 text-center">
            <div className="size-6 flex items-center justify-center">
              <img src="/iSource LOGO1.png" alt="iSource" className="w-full h-full object-contain opacity-60" />
            </div>
            <p className="text-sm text-ink-soft">No itineraries plotted for this day yet.</p>
          </div>
        )}
        {items.map((it) => {
          const writable = canEditItinerary ? canEditItinerary(it) : true;
          return (
            <div
              key={it.id}
              className={cn(
                "group flex items-center gap-3 rounded-xl border border-line bg-paper/60 px-3 py-2.5 transition-colors",
                writable ? "hover:border-ink/25" : "opacity-90",
              )}
            >
              <EmployeeDot name={it.employeeName ?? "?"} color={it.employeeColor ?? "#999"} size={28} />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  {it.appointmentTime && (
                    <span className="shrink-0 rounded bg-ink/[0.07] px-1.5 py-0.5 text-[10px] font-bold text-ink-soft tabular-nums">
                      {formatTime(it.appointmentTime)}
                    </span>
                  )}
                  <span className="truncate text-sm font-semibold">{it.title}</span>
                  {!writable && (
                    <span
                      title="Read-only — this itinerary is bound to another account."
                      className="shrink-0 text-[10px] text-ink-faint"
                    >
                      🔒
                    </span>
                  )}
                </div>
                <div className="truncate text-[11px] text-ink-faint">
                  {it.employeeName} · {ordinal(it.seq)} stop
                  {it.locationName ? ` · ${it.locationName}` : ""}
                  {it.notes ? ` · ${it.notes}` : ""}
                </div>
              </div>
              <StatusPill status={it.status} small />
              {writable && (
                <div className="flex items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100">
                  <button
                    type="button"
                    title="Edit"
                    onClick={() => onEdit(it)}
                    className="grid size-7 place-items-center rounded-md text-ink-faint transition-colors hover:bg-ink/10 hover:text-ink"
                  >
                    <Pencil className="size-3.5" />
                  </button>
                  <ConfirmDelete onConfirm={() => onDelete(it)} />
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div className="pt-4">
        <PrimaryButton
          onClick={onAdd}
          icon={Plus}
          disabled={!hasWritableEmployee && !isPrivileged}
          className="w-full justify-center"
        >
          {isPrivileged || hasWritableEmployee
            ? "Add itinerary for this day"
            : "🔒 No bound profile"}
        </PrimaryButton>
      </div>
    </Modal>
  );
}
